import React from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from './PublicHeader';
import { supabase } from './supabaseClient';
import './PublicHome.css';
import './Contact.css';

function Contact() {
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [topic, setTopic] = React.useState('general');
  const [message, setMessage] = React.useState('');

  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState('');
  const [submitted, setSubmitted] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError('');

    if (!supabase) {
      setSaveError(
        'Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env.local.'
      );
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from('contact_messages').insert({
        full_name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        topic,
        message: message.trim(),
      });
      if (error) throw error;
      setName('');
      setEmail('');
      setPhone('');
      setTopic('general');
      setMessage('');
      setSubmitted(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setSaveError(
        err?.message ||
          err?.error_description ||
          'Could not send your message. Run sql/add_contact_messages_supabase.sql in Supabase.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSendAnother = () => {
    setSubmitted(false);
    setSaveError('');
  };

  return (
    <div className="ph-root ct-page">
      <PublicHeader />

      <main className="ct-main">
        <header className="ct-hero">
          <p className="ph-eyebrow">We’re here to help</p>
          <h1 className="ct-title">Contact us</h1>
          <p className="ct-lead">
            Questions about listings, viewings, or your tenancy? Send us a note
            and we’ll respond as soon as we can.
          </p>
        </header>

        <div className="ct-grid">
          <section className="ct-card ct-card--form" aria-label="Contact form">
            <h2 className="ct-card-title">Send a message</h2>

            {submitted ? (
              <div className="ct-success-banner">
                <p className="ct-success-title">Thanks — we’ve received your message.</p>
                <p className="ct-success-text">
                  It’s been saved securely. Our team will get back to you as soon as we can.
                </p>
                <button
                  type="button"
                  className="ph-btn ph-btn-outline ct-success-btn"
                  onClick={handleSendAnother}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form className="ct-form" onSubmit={handleSubmit}>
                {saveError ? (
                  <p className="ct-save-error" role="alert">
                    {saveError}
                  </p>
                ) : null}
                <div className="ct-field">
                  <label className="ct-label" htmlFor="ct-name">
                    Name
                  </label>
                  <input
                    id="ct-name"
                    className="ct-input"
                    type="text"
                    autoComplete="name"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                  />
                </div>
                <div className="ct-field">
                  <label className="ct-label" htmlFor="ct-email">
                    Email
                  </label>
                  <input
                    id="ct-email"
                    className="ct-input"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                  />
                </div>
                <div className="ct-field">
                  <label className="ct-label" htmlFor="ct-phone">
                    Phone (optional)
                  </label>
                  <input
                    id="ct-phone"
                    className="ct-input"
                    type="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+44 …"
                  />
                </div>
                <div className="ct-field">
                  <label className="ct-label" htmlFor="ct-topic">
                    Topic
                  </label>
                  <select
                    id="ct-topic"
                    className="ct-input ct-select"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                  >
                    <option value="general">General enquiry</option>
                    <option value="viewing">Book a viewing</option>
                    <option value="tenancy">Existing tenancy</option>
                    <option value="list">List a property</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="ct-field">
                  <label className="ct-label" htmlFor="ct-message">
                    Message
                  </label>
                  <textarea
                    id="ct-message"
                    className="ct-textarea"
                    rows={5}
                    required
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="How can we help?"
                  />
                </div>
                <button
                  type="submit"
                  className="ph-btn ph-btn-primary ct-submit"
                  disabled={saving}
                >
                  {saving ? 'Sending…' : 'Send message'}
                </button>
                <p className="ct-footnote">Your message is stored in our database for the team to review.</p>
              </form>
            )}
          </section>

          <aside className="ct-card ct-card--aside" aria-label="Other ways to reach us">
            <h2 className="ct-card-title">Other ways to reach us</h2>
            <ul className="ct-aside-list">
              <li>
                <span className="ct-aside-label">Email</span>
                <a
                  href="mailto:Management@odusProperties.London"
                  className="ct-aside-value"
                >
                  Management@odusProperties.London
                </a>
              </li>
              <li>
                <span className="ct-aside-label">Phone</span>
                <a href="tel:+441707243870" className="ct-aside-value">
                  01707 243870
                </a>
              </li>
              <li>
                <span className="ct-aside-label">Registered office address</span>
                <span className="ct-aside-value ct-aside-value--muted ct-aside-address">
                  Propfield Farm, Hatfield Road, Smallford, St Albans, Hertfordshire,
                  England, AL4 0HW
                </span>
              </li>
              <li>
                <span className="ct-aside-label">Hours</span>
                <span className="ct-aside-value ct-aside-value--muted">
                  Mon–Fri, 9:00–17:30
                </span>
              </li>
            </ul>
          </aside>
        </div>
      </main>
    </div>
  );
}

export default Contact;
